/*
 * @Author: yidan.chen
 * @Date: 2025-02-14 14:03:12
 * @Last Modified by: yidan.chen
 * @Last Modified time: 2025-05-02 10:29:06
 */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider
} from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Error from '@mui/icons-material/Error'
import Warning from '@mui/icons-material/Warning'
import History from '@mui/icons-material/History'
import Delete from '@mui/icons-material/Delete'
import Info from '@mui/icons-material/Info'

type DialogType =
  | 'success'
  | 'error'
  | 'warning'
  | 'confirm'
  | 'history'
  | 'delete'
  | 'info'
const iconMap = {
  success: <CheckCircle style={{ color: '#4CAF50', fontSize: 26 }} />,
  error: <Error style={{ color: '#F44336', fontSize: 26 }} />,
  warning: <Warning style={{ color: '#FF9800', fontSize: 26 }} />,
  confirm: <CheckCircle style={{ color: '#4CAF50', fontSize: 26 }} />,
  history: <History style={{ color: '#002678', fontSize: 26 }} />,
  delete: <Delete style={{ color: '#D32F2F', fontSize: 26 }} />,
  info: <Info style={{ color: '#002678', fontSize: 26 }} />
}

export interface IProps {
  open: boolean
  onConfirm?: () => void
  onClose?: () => void
  title: string
  children?: React.ReactNode
  type: DialogType
  noContentPadding?: boolean
  cancelText?: string
  confirmText?: string
  width?: string
  /** Optional sx override for the outer MUI Dialog component */
  dialogSx?: Record<string, unknown>
  titleTestId?: string
  confirmBtnTestId?: string
  cancelBtnTestId?: string
  confirmDisabled?: boolean
}

const ConfirmDialog: React.FC<IProps> = props => {
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      sx={{
        minWidth: props.width || '600px',
        width: '100%',
        '& .MuiDialog-paper': {
          minWidth: props.width || '600px'
        },
        ...(props.dialogSx ?? {})
      }}
      PaperProps={{
        sx: {
          overflow: 'clip'
        }
      }}>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start'
          }}>
          {props.type !== 'confirm' && iconMap[props.type]}
          <Typography
            data-testid={props.titleTestId}
            sx={{
              fontWeight: '600',
              fontSize: '20px',
              textAlign: 'center',
              marginLeft: props.type === 'confirm' ? '0px' : '12px'
            }}
            component='div'>
            {props.title}
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent
        sx={{
          padding: props.noContentPadding ? '0px' : '16px 24px 16px 24px'
        }}>
        {props.children}
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{ padding: '16px 24px 16px 24px', justifyContent: 'end' }}>
        <Button
          data-testid={props.cancelBtnTestId}
          onClick={props.onClose}
          variant='text'
          sx={{ minWidth: '62px', height: '36px', alignSelf: 'center' }}>
          <Typography fontSize={14} color='primary' fontWeight='bold'>
            {props.cancelText || 'Cancel'}
          </Typography>
        </Button>
        {props.onConfirm && (
          <Button
            data-testid={props.confirmBtnTestId}
            onClick={props.onConfirm}
            variant='contained'
            disabled={props.confirmDisabled}
            color={props.type === 'delete' ? 'error' : 'primary'}
            sx={{ minWidth: '86px', height: '36px', alignSelf: 'center' }}>
            <Typography fontSize={14} color='#fff' fontWeight='bold'>
              {props.confirmText || 'Confirm'}
            </Typography>
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmDialog
